import Cookies from "js-cookie";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import ProcessingLoader from "../components/ProcessingLoader";
import {
  handleAuthError,
  refreshAccessToken,
  verifyToken,
} from "../helper/authService";
import { checkRoutePermissions } from "../helper/permissionService";
import { ROUTES_PATH } from "../utils/EndPoints";
import { ENUM_EVENT } from "../utils/eventEmitter";
import { useTabScopedIdleLogout } from "../hooks/useTabScopedIdleLogout";
import { permissionsFn } from "../helper/com-helper";
const PrivateRoute = ({ children, path }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isTokenValid, setTokenValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invalidToken, setInvalidToken] = useState(false);
  const [validRoute, setValidRoute] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || null);
  const permissions = permissionsFn(user)

  const resetSettings = (refreshError) => {
    setLoading(false);
    sessionStorage.clear();
    navigate(ROUTES_PATH.ROUTE);
    handleAuthError((refreshError || "SESSION_EXPIRED"), navigate, dispatch);
  };

  // ⏳ Idle logout logic (10 min inactivity in this tab only)
  useTabScopedIdleLogout({
    logoutFn: resetSettings,
    idleMs: 29 * 60 * 1000 + 30 * 1000, // 10 minutes
    countNetworkActivityInThisTab: false, // change to true if you want network calls to reset timer
  });

  const reVerifyToken = async () => {
    try {
      if (!user) throw new Error();
      const authenticator = localStorage.getItem("authenticator");
      const isRefreshed = await refreshAccessToken(null, authenticator);
      const hasPermission = checkRoutePermissions(path, permissions);
      setValidRoute(hasPermission);
      setTokenValid(isRefreshed);
      setLoading(false);
    } catch (refreshError) {
      resetSettings(refreshError);
    }
  };

  const _verifyTokenAndPermissions = async (invalidToken) => {
    try {
      if (invalidToken) throw new Error("TOKEN_NOT_VALID");
      const isVerified = await verifyToken();
      setTokenValid(isVerified);

      const hasPermission = checkRoutePermissions(path, permissions);
      setValidRoute(hasPermission);

      setLoading(false);
    } catch (error) {
      reVerifyToken();
    }
  };

  useEffect(() => {
    const handleTokenEvent = (event) => {
      setInvalidToken(event?.detail?.isInvalid);
    };

    window.addEventListener(ENUM_EVENT.INVALID_TOKEN, handleTokenEvent);
    return () => {
      window.removeEventListener(ENUM_EVENT.INVALID_TOKEN, handleTokenEvent);
    };
  }, []);

  useEffect(() => {
    _verifyTokenAndPermissions(invalidToken);
  }, [invalidToken]);

  useEffect(() => {
    const interval = setInterval(() => {
      reVerifyToken();
    }, 55 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <ProcessingLoader />;
  }

  if (validRoute && isTokenValid) {
    if (path.includes("/:")) {
      const _path = location.pathname;
      localStorage.setItem("childRoute", _path);
    } else {
      localStorage.setItem("childRoute", path);
    }
    return children;
  }

  return <Navigate to={validRoute ? "/" : "/error"} />;
};

PrivateRoute.propTypes = {
  children: PropTypes.node,
  path: PropTypes.string,
};

export default PrivateRoute;
